import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import hash from 'object-hash'

import { Palette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  getMultipleLineChartProps,
  getDataSources,
  CompassAPIMultiSeriesChartData,
} from '../utils/compass'
import type { BaseContext } from './'
import { commonChartOptions } from './common'

export namespace ArtificialEtfs {
  export namespace TopBottomAtts {
    const CONTEXT_ID = 'equity:artificial_etfs:top_bottom_atts'
    const TOP_BOTTOM_PATH =
      '/api/charts/artificial-etfs/analysis/top-bottom-atts'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chartKey: string
      displayName: string
      startDate: string
      endDate: string
      title?: string
    }

    interface TopBottomPoint {
      ticker: string
      value: number
    }

    export interface ChartData extends CompassAPIDataBase {
      data: {
        bottom: TopBottomPoint[]
        top: TopBottomPoint[]
      }
      title: string
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url: TOP_BOTTOM_PATH,
          method: 'POST',
          data: {
            key: config.chartKey,
            start_date: config.startDate,
            end_date: config.endDate,
          },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = [
        {
          data: data
            ? [
                ...data.data.top,
                { ticker: '', value: 0 },
                ...data.data.bottom,
              ].map((column, idx) => ({
                name: column.ticker,
                y: column.value * 100,
                color:
                  column.value >= 0
                    ? '#30ba09' // success.main
                    : '#f4643d', // error.light
                ...(idx === data.data.top.length
                  ? { dataLabels: { enabled: false } }
                  : {}),
              }))
            : [],
          dataLabels: { enabled: true, format: '{point.y:.2f} %' },
          color: Palette.green,
          showInLegend: false,
          yAxis: 'primary',
        },
      ]

      const tooltip = {
        shared: true,
        split: undefined,
        valueDecimals: 2,
        valueSuffix: '%',
      }

      const xAxis = [
        {
          type: 'category' as const,
          plotLines: [
            data
              ? {
                  dashStyle: 'LongDash' as const,
                  value: data.data.top.length,
                  label: {
                    formatter: () => {
                      const top =
                        data.data.top.length > 0
                          ? `Top ${data.data.top.length}`
                          : ''
                      const bottom =
                        data.data.bottom.length > 0
                          ? `Bottom ${data.data.bottom.length}`
                          : ''
                      return `${top}<br />${bottom}`
                    },
                  },
                  zIndex: 10,
                }
              : {},
          ],
        },
      ]

      const yAxis = [
        { id: 'primary', title: { text: '' }, labels: { format: '{value} %' } },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      return {
        id: Context.getChartId(config),
        title: `Top bottom attr. - ${config.displayName}`,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            chart: { type: 'bar' },
            series,
            yAxis,
            xAxis,
            plotOptions: { column: { stacking: 'normal' } },
            tooltip,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chartKey}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Performance {
    const CONTEXT_ID = 'equity:artificial_etfs:performance'
    const PERFORMANCE_PATH =
      '/api/charts/artificial-etfs/analysis/performance-graph'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chartKey: string
      displayName: string
      startDate: string
      endDate: string
      title?: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url: PERFORMANCE_PATH,
          method: 'POST',
          data: {
            key: config.chartKey,
            start_date: config.startDate,
            end_date: config.endDate,
          },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: `Performance - ${config.displayName}`,
          convertToPercentages: Array(data.data.length).fill(true),
          tooltip: { xDateFormat: '%A, %b %e, %Y' },
          xAxis: { labels: { format: '{value:%Y}' } },
        },
        { name: [''] }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chartKey}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace MultiplePerformance {
    const CONTEXT_ID = 'equity:artificial_etfs:multiple_performance'
    const PERFORMANCE_PATH =
      '/api/charts/artificial-etfs/analysis/multiple-performance-graph'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      sector: string
      startDate: string
      endDate: string
      title?: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url: PERFORMANCE_PATH,
          method: 'POST',
          data: {
            sector: config.sector,
            start_date: config.startDate,
            end_date: config.endDate,
          },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        chartTitle: `${config.sector} - Expanded IBOV Weighted`,
        convertToPercentages: Array(data.data.length).fill(true),
        tooltip: { xDateFormat: '%A, %b %e, %Y' },
        xAxis: { labels: { format: '{value:%Y}' } },
      })(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.sector}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
